import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';

import {
  createLoginCalendar as createLoginCalendarReduxAction,
  deleteLoginCalendar as deleteLoginCalendarReduxAction,
} from 'containers/App/actions';
import Select from 'components/FormFields/Select';
import FormCheckbox from 'components/FormFields/FormCheckbox';
import Clickwrap from 'components/Clickwrap';
import {
  getExternalCalendars,
  deleteLoginCalendar,
  addLoginCalendar,
  getSyncedWeddings,
  toggleCalendarExternalSync,
  createExternalBookedLeadsEvents,
  deleteExternalBookedLeadsEvents,
} from 'api/calendar';
import { useAction } from 'hooks/useAction';
import { usePushSiteError } from 'hooks/usePushSiteError';
import {
  CREATE_LOGIN_CALENDAR_SUCCESS,
  DELETE_LOGIN_CALENDAR_SUCCESS,
} from 'containers/App/constants';

import './style.scss';
import { useFloatingTooltip } from 'hooks/useFloatingTooltip';
import { usePrefs } from 'hooks/usePrefs';


const INTERNAL_CALENDAR_TYPES = [
  {
    value: 'tentative_bookings',
    label: 'Tentative Bookings',
    name: 'Tentative Bookings',
  },
  {
    value: 'projects',
    label: 'Booked Projects',
    name: 'Booked Projects',
  },
  // {
  //   value: 'invoices',
  //   label: 'Invoices',
  //   name: 'AislePlanner Invoice',
  /// },
];

const CalendarSyncSettings = ({ onCalendarSyncSettingsToggle }) => {
  const dispatch = useDispatch();
  const [calendars, setCalendars] = useState([]);
  const [syncedProjects, setSyncedProjects] = useState([]);
  const [isSyncing, setIsSyncing] = useState({});
  const [isAnySynced, setAnySynced] = useState(false);
  const [isAnyShowing, setAnyShowing] = useState(false);

  const userInfo = useSelector((state) => state.global.userInfo);

  const { updateCalendarPrefs, calendar: calendarPrefs } = usePrefs();
  const { external } = calendarPrefs || {};
  const { includeBookedLeads } = external || {};
  const pushSiteError = usePushSiteError();
  const { integrations, loginWeddings, loginCalendarSyncing, id: loginId, effectivePermissions } = userInfo || {};

  const canAccessLeads = effectivePermissions['lead.all'] || effectivePermissions['lead.full-access'];

  // #TODO: decouple the component calendar from the calendar page.
  // To prevent error in storybook.
  if (!userInfo) {
    return null;
  }

  // TODO: Need to modify this if we allow multiple integrations per account
  const integration = integrations.filter(i => i.grantStatus === 'valid')[0] ?? null;

  const { loginCalendars = [] } = integration || {};
  const syncableInternalCalendars = INTERNAL_CALENDAR_TYPES.map(({ value, label, name }) => {
    const matchedCalendar = loginCalendars.find(calendar => calendar.calendarType === value);
    return {
      value,
      label,
      name,
      isSynced: !!matchedCalendar,
      externalCalendar: matchedCalendar || null,
    };
  });
  const isInternalProjectsCalendarSyncable = loginCalendars.some(calendar => calendar.calendarType === 'projects');

  const createCalendarWithRedux = useAction(createLoginCalendarReduxAction);
  const deleteCalendarWithRedux = useAction(deleteLoginCalendarReduxAction);

  const {
    tooltipElement: syncAPTooltip,
    showTooltip: showSyncAPTooltip,
    hideTooltip: hideSyncAPTooltip,
    updateTooltip: updateSyncAPTooltip,
  } = useFloatingTooltip(
    'Edits made to Aisle Planner events in your external calendar won’t sync back to Aisle Planner.',
  );
  const {
    tooltipElement: showExternalTooltip,
    showTooltip: showExternalCalendarTooltip,
    hideTooltip: hideExternalCalendarTooltip,
    updateTooltip: updateExternalCalendarTooltip,
  } = useFloatingTooltip(
    'Events imported from your external calendar are view-only and can’t be edited in Aisle Planner.',
  );

  const fetchCalendars = async () => {
    try {
      const [calendarResponse, syncedResponse] = await Promise.all([
        getExternalCalendars(),
        getSyncedWeddings(),
      ]);
      if (calendarResponse.ok && Array.isArray(calendarResponse.data)) {
        setCalendars(calendarResponse.data.filter(item => item.name !== 'Tentative Bookings'));
      } else {
        // eslint-disable-next-line no-console
        console.error('Invalid response data for calendars');
      }
      if (syncedResponse.ok && Array.isArray(syncedResponse.data)) {
        setSyncedProjects(syncedResponse.data);
      } else {
        // eslint-disable-next-line no-console
        console.error('Unable to fetch synced calendars');
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to fetch external calendars:', error);
    }
  };

  const formatLoginCalendar = (calendar) => ({
    id: calendar.id,
    calendarsId: calendar.calendars_id,
    integrationId: calendar.integration_id,
    calendarType: calendar.calendar_type,
    name: calendar.name,
    hexColor: calendar.hex_color,
  });

  const handleToggle = async (calendar, value) => {
    const loginCalendarId = calendar?.login_calendar_id;
    let isSynced = !!loginCalendarId;

    if (value !== undefined) {
      isSynced = !value;
    }

    setIsSyncing((prev) => ({ ...prev, [calendar.id]: true }));
    try {
      const response = isSynced
        ? await deleteLoginCalendar(loginCalendarId)
        : await addLoginCalendar({
          'integrationId': calendar.integration_id,
          'calendarsId': calendar.id,
        });

      if (response.ok) {
        dispatch({
          type: isSynced
            ? DELETE_LOGIN_CALENDAR_SUCCESS
            : CREATE_LOGIN_CALENDAR_SUCCESS,
          payload: {
            loginCalendar: isSynced
              ? {
                  id: calendar.login_calendar_id,
                  integrationId: calendar.integration_id,
                }
              : formatLoginCalendar(response.data),
          },
        });

        onCalendarSyncSettingsToggle(response.data?.id, !isSynced);

        setCalendars((prevCalendars) =>
          prevCalendars.map((c) =>
            c.id === calendar.id
              ? { ...c, login_calendar_id: isSynced ? null : response.data?.id }
              : c,
          ),
        );
      } else {
        // eslint-disable-next-line no-console
        console.error('Failed to toggle calendar sync:', response);
        return { ok: false, error: response.error || 'Failed to toggle calendar sync' };
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error toggling calendar sync:', error);
      return { ok: false, error: error.message || 'An error occurred while toggling calendar sync' };
    }
    setIsSyncing((prev) => ({ ...prev, [calendar.id]: false }));

    return { ok: true, data: calendar };
  };

  async function handleToggleRecursive(_calendars, value) {
    if (_calendars.length === 0) return;

    const calendar = _calendars[0];
    try {
      const result = await handleToggle(calendar, value);

      if (result.ok) {
        // Remove the first calendar from the list and continue with the rest
        return handleToggleRecursive(_calendars.slice(1), value);
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error toggling calendar:', error);
      if (_calendars.length > 1) {
        // If there are more calendars to process, continue with the next one
        return handleToggleRecursive(_calendars.slice(1), value);
      }
      return { ok: false, error: error.message || 'An error occurred while toggling calendar' };
    }
  }

  // TODO: There is a larger issue with this function.
  // We worked around it by using updateCalendarPrefs.
  // Ideally the handleToggle is able to guarantee the
  // calendar prefs are updated in sync with the method.
  const handleUnshowAll = async () => {
    // We only want to try to unsync calendars that are synced, otherwise the toggle fails.
    const syncedCalendars = filteredCalendars.filter(cal => cal.login_calendar_id);
    try {
      setIsSyncing((prev) => ({ ...prev, ...Object.fromEntries(calendars.map(calendar => [calendar.id, true])) }));
      await handleToggleRecursive(syncedCalendars, false);
      updateCalendarPrefs('externalCalendarsDisplay', {});
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error unshowing all calendars:', error);
    } finally {
      setIsSyncing((prev) => ({ ...prev, ...Object.fromEntries(calendars.map(calendar => [calendar.id, false])) }));
    }
  };

  const toggleAnyShowing = () => {
    if (isAnyShowing) {
      // If we are turning off showing, we need to hide all calendars
      handleUnshowAll().then(() => {
        setAnyShowing(false);
      });
    } else {
      setAnyShowing((prev) => !prev);
    }
  };

  const handleUnsyncAll = async () => {
    setIsSyncing((prev) => ({
      ...prev,
      ...Object.fromEntries(syncableInternalCalendars.map(cal => [cal.value, true])),
    }));
    syncableInternalCalendars.forEach(cal => {
      try {
        if (cal.externalCalendar?.id) {
          deleteCalendarWithRedux(cal.externalCalendar.id);
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error(`Error deleting calendar ${cal.name}:`, error);
      }
    });
    setIsSyncing((prev) => ({
      ...prev,
      ...Object.fromEntries(syncableInternalCalendars.map(cal => [cal.value, false])),
    }));
  };

  const toggleIsAnySynced = () => {
    if (isAnySynced) {
      // If we are turning off syncing, we need to unsync all calendars
      handleUnsyncAll().then(() => {
        setAnySynced(false);
      });
    } else {
      setAnySynced((prev) => !prev);
    }
  };

  const toggleInternalCalSync = (e, cal) => {
    e.preventDefault();
    const { isSynced, value: calendarType, name: calName, externalCalendar } = cal || {};
    const { id: calId } = externalCalendar || {};
    if (isSynced && calId) {
      deleteCalendarWithRedux(calId);
    } else {
      createCalendarWithRedux({ integrationId: integration.id, calendarType, name: calName });
    }
  };

  const toggleProjectToExternalSync = async (e, weddingIds) => {
    e.preventDefault();

    const projectResults = await Promise.all(
      weddingIds.map(async (weddingId) => {
        const projectResult = await toggleCalendarExternalSync(
          {
            weddingId: weddingId.id,
            loginId,
            checked: !weddingId.checked,
          },
        );
        return { result: projectResult, wedding_id: weddingId.id };
      }),
    );

    const okProjects = projectResults.filter(r => r.result.ok);
    const errProjects = projectResults.filter(r => !r.result.ok);

    setSyncedProjects([
      ...syncedProjects.filter(
        syncedProject => !okProjects.some(project => project.wedding_id === syncedProject.wedding_id),
      ),
      ...okProjects.map(project => ({
        wedding_id: project.wedding_id,
        loginId,
        integrations_sync_enabled: project.result.data.integrations_sync_enabled,
      })),
    ]);

    if (errProjects.length > 0) {
      pushSiteError('Your project couldn\'t be synced to your external calendar. Please try again in a moment.');
    }
  };

  const handleBookedLeadsToggle = async (e) => {
    const { target } = e;
    const { checked } = target || {};
    let results;
    target.setAttribute('disabled', true);
    target.classList.add('loading');
    if (checked) {
      results = await createExternalBookedLeadsEvents();
    } else {
      results = await deleteExternalBookedLeadsEvents();
    }
    if (results.ok) {
      updateCalendarPrefs('external.includeBookedLeads', checked);
    }
    else {
      pushSiteError('Your booked leads couldn\'t be deleted. Please try again in a moment.');
      target.setAttribute('checked', !checked);
    }
    target.removeAttribute('disabled');
    target.classList.remove('loading');
  };

  const provider = integration?.provider ?
    integration.provider.charAt(0).toUpperCase() + integration.provider.slice(1) :
    'External Calendar';

  // Filter out external calendars that are already synced with internal calendars
  const syncableInternalCalendarIds =
    syncableInternalCalendars.map(cal => cal?.externalCalendar?.id || null).filter(Boolean);
  const filteredCalendars = calendars.filter(calendar => {
    return !syncableInternalCalendarIds.includes(calendar.login_calendar_id) || true;
  });

  useEffect(() => {
    fetchCalendars();
  }, []);

  useEffect(() => {
    const anySynced = syncableInternalCalendars.some(project => project.isSynced);
    setAnySynced(anySynced);
    const anyShowing = filteredCalendars.some(calendar => !!calendar.login_calendar_id);
    setAnyShowing(anyShowing);
  }, [calendars, syncedProjects]);

  if (!calendars.length) return null;

  return (
    <>
      {syncAPTooltip}
      {showExternalTooltip}
      <div className="view-options__section calendar-sync-settings">
        <div className="view-option view-option--checkbox view-option--header">
          <Clickwrap
            name="calendar-sync-settings-toggle"
            checked={isAnySynced}
            className="clickwrap--view-option external-calendar small"
            onChange={toggleIsAnySynced}
          />
          <label
            htmlFor="calendar-sync-settings-toggle"
            className="view-option__label"
          >
            Sync Aisle Planner calendars to {provider} calendar
          </label>
          <span
            className="view-option__tooltip"
            onMouseMove={updateSyncAPTooltip}
            onMouseLeave={hideSyncAPTooltip}
            onMouseEnter={showSyncAPTooltip}
          >!</span>
        </div>
        {isAnySynced && (
          <div className="margin-l-5">
            <div className="view-options__subtitle padding-l-5">
              {`Select the Calendars you'd like to Show in ${provider}:`}
            </div>
            {syncableInternalCalendars.map(({
              value,
              label,
              name,
              isSynced,
              externalCalendar,
            }, index) => (
              <div
                key={value}
                className="view-option view-option--checkbox view-option--internal-calendar padding-l-5"
              >
                <Clickwrap
                  name={`internal-calendar-option-checkbox-${index}`}
                  checked={isSynced}
                  className="clickwrap--view-option external-calendar small"
                  onChange={(e) =>
                    !loginCalendarSyncing?.[value] &&
                    toggleInternalCalSync(e, { name, value, isSynced, externalCalendar })
                  }
                />
                <label
                  htmlFor={`internal-calendar-option-checkbox-${index}`}
                  className="view-option__label"
                >
                  {label}
                </label>
                {loginCalendarSyncing?.[value] && (
                  <>&nbsp;<span className="fa fa-spin fa-spinner" /></>
                )}
              </div>
            ))}
            {isInternalProjectsCalendarSyncable && (
              <InternalProjectSyncDropdown
                loginWeddings={loginWeddings?.items}
                syncedProjects={syncedProjects}
                handleToggle={toggleProjectToExternalSync}
                bookedLeadsSynced={includeBookedLeads}
                toggleBookedLeads={handleBookedLeadsToggle}
                canAccessLeads={canAccessLeads}
              />
            )}
          </div>
        )}
      </div>
      <div className="view-options__section calendar-sync-settings">
        <div className="view-option view-option--checkbox view-option--header">
          <Clickwrap
            name="calendar-show-settings-toggle"
            checked={isAnyShowing}
            className="clickwrap--view-option external-calendar small"
            onChange={toggleAnyShowing}
          />
          <label
            htmlFor="calendar-sync-settings-toggle"
            className="view-option__label"
          >
            Show {provider} calendars in Aisle Planner
          </label>
          <span
            className="view-option__tooltip"
            onMouseMove={updateExternalCalendarTooltip}
            onMouseLeave={hideExternalCalendarTooltip}
            onMouseEnter={showExternalCalendarTooltip}
          >!</span>
        </div>

        { isAnyShowing && (
          <div className="margin-l-5">
            <div className="view-options__subtitle padding-l-5">
              Select the Calendars you'd like to show in<br />Aisle Planner:
            </div>
            {
              filteredCalendars.map((calendar, index) => (
                <div
                  key={calendar.id}
                  className="view-option view-option--checkbox view-option--external-calendar padding-l-5"
                >
                  <Clickwrap
                    name={`external-calendar-option-checkbox-${index}`}
                    checked={!!calendar?.login_calendar_id}
                    className="clickwrap--view-option external-calendar small"
                    onChange={() => handleToggle(calendar)}
                  />
                  <label
                    htmlFor={`external-calendar-option-checkbox-${index}`}
                    className="view-option__label"
                  >
                    {calendar.name}
                  </label>
                  {isSyncing[calendar.id] && (
                    <>&nbsp;<span className="fa fa-spin fa-spinner" /></>
                  )}
                </div>
              ))
            }
          </div>
        )}
      </div>
    </>
  );
};

const InternalProjectSyncDropdown = ({
  loginWeddings = [],
  syncedProjects = [],
  handleToggle,
  bookedLeadsSynced,
  toggleBookedLeads,
  canAccessLeads = false,
}) => {
  // Combine the partial synced projects list state into the loginWeddings for checked status.
  const weddings = loginWeddings.map(lw => {
    const found = syncedProjects.find(project => lw.weddingId === project.wedding_id);
    return { ...lw, checked: found && found.integrations_sync_enabled || false };
  });

  function toggleAll(e) {
    e.preventDefault();
    const allChecked = weddings.every(wedding => wedding.checked);
    const messageIds = weddings.map(wedding => ({ id: wedding.weddingId, checked: allChecked }));
    handleToggle(e, messageIds);
    toggleBookedLeads(e);
  };

  return (
    <Select
      showSearch
      parentClassName="margin-l-10 margin-r-10"
      preventFlip
    >
      <FormCheckbox
        name="internal-calendar-to-sync-all"
        onChange={(e) => toggleAll(e)}
        checked={weddings.every(wedding => wedding.checked)}
        wrapperProps={{
          className: 'small-checkbox padding-x-3',
        }}
        labelProps={{
          className: 'form-item__label--unstyled',
        }}
      >
        Select All
      </FormCheckbox>
      {canAccessLeads && (
        <FormCheckbox
          name="internal-calendar-to-booked-leads"
          onChange={(e) => toggleBookedLeads(e)}
          checked={bookedLeadsSynced}
          wrapperProps={{
            className: 'small-checkbox padding-x-3',
          }}
          labelProps={{
            className: 'form-item__label--unstyled',
          }}
        >
          Booked Leads
        </FormCheckbox>
      )}
      {weddings.sort((a, b) => a.weddingName.toUpperCase() > b.weddingName.toUpperCase() ? 1 : -1).map(wedding => (
        <FormCheckbox
          key={`internal-calendar-to-sync-${wedding.weddingId}`}
          name={`internal-calendar-to-sync-${wedding.weddingId}`}
          onChange={e => handleToggle(e, [{ id: wedding.weddingId, checked: wedding.checked }])}
          checked={wedding.checked}
          wrapperProps={{
            className: 'small-checkbox padding-x-3',
          }}
          labelProps={{
            className: 'form-item__label--unstyled',
          }}
        >
          {wedding.weddingName}
        </FormCheckbox>
      ))}
    </Select>
  );
};

export default CalendarSyncSettings;
